'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Upload, Zap, RefreshCw, ChevronRight, Undo2, ArrowRight, Settings, ExternalLink } from 'lucide-react';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { useMondayAuth, useSubscriptionTier } from '@/contexts/MondayAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { StatusBadge, isProcessingStatus, isTerminalStatus } from '@/components/operations/StatusBadge';
import { OperationProgressBar, SimpleProgressBar } from '@/components/operations/OperationProgressBar';
import type { OperationStatus } from '@/lib/services/bulk-operations/types';
import type { LucideIcon } from 'lucide-react';

// Force dynamic rendering
export const dynamic = 'force-dynamic';

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
}

function StatCard({ title, value, change, changeType = 'neutral' }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-[var(--text-secondary)]">{title}</p>
        <p className="text-3xl font-bold font-heading text-[var(--text-heading)] mt-1">{value}</p>
        {change && (
          <p
            className={`text-sm mt-1 ${
              changeType === 'positive'
                ? 'text-green-600'
                : changeType === 'negative'
                ? 'text-red-600'
                : 'text-[var(--text-muted)]'
            }`}
          >
            {change}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// FEATURE ACTION CARD (New User)
// =============================================================================

interface FeatureActionCardProps {
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  tier: string;
  href: string;
  ctaLabel: string;
}

function FeatureActionCard({ icon: Icon, iconBg, iconColor, title, description, tier, href, ctaLabel }: FeatureActionCardProps) {
  return (
    <Card interactive>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-11 h-11 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-semibold text-[var(--text-heading)]">{title}</h3>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">{description}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">{tier}</p>
            <Link
              href={href}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 mt-2"
            >
              {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// USAGE PANEL (Free)
// =============================================================================

interface UsagePanelFreeProps {
  usageStats: {
    bulkOperations: { used: number; limit: number };
    recurringPatterns: { used: number; limit: number };
    csvImports: { used: number; limit: number };
  } | null;
  onUpgrade: () => void;
}

function UsagePanelFree({ usageStats, onUpgrade }: UsagePanelFreeProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading">This Month&apos;s Usage</CardTitle>
          <Badge variant="outline">free</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <UsageMeter
          label="Bulk Operations"
          used={usageStats?.bulkOperations.used ?? 0}
          limit={usageStats?.bulkOperations.limit === -1 ? 'unlimited' : (usageStats?.bulkOperations.limit ?? 5)}
        />
        <UsageMeter
          label="Recurring Tasks"
          used={usageStats?.recurringPatterns.used ?? 0}
          limit={usageStats?.recurringPatterns.limit === -1 ? 'unlimited' : (usageStats?.recurringPatterns.limit ?? 10)}
        />
        <UsageMeter
          label="CSV Imports"
          used={usageStats?.csvImports.used ?? 0}
          limit={usageStats?.csvImports.limit === -1 ? 'unlimited' : (usageStats?.csvImports.limit ?? 'unlimited')}
        />
        <div className="pt-4 border-t border-[var(--border-default)]">
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            Need more? Upgrade for unlimited access.
          </p>
          <button
            onClick={onUpgrade}
            className="block w-full py-2 px-4 bg-primary-600 text-white text-center rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Upgrade to Pro
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// USAGE PANEL (Pro)
// =============================================================================

interface UsagePanelProProps {
  usageStats: {
    bulkOperations: { used: number; limit: number };
    recurringPatterns: { used: number; limit: number };
    csvImports: { used: number; limit: number };
  } | null;
  activeRecurring: number;
}

function UsagePanelPro({ usageStats, activeRecurring }: UsagePanelProProps) {
  const proStats = [
    { label: 'Bulk Operations', value: usageStats?.bulkOperations.used ?? 0 },
    { label: 'Recurring Tasks', value: `${activeRecurring} active` },
    { label: 'CSV Imports', value: usageStats?.csvImports.used ?? 0 },
    { label: 'Items Processed', value: (usageStats?.bulkOperations.used ?? 0) + (usageStats?.csvImports.used ?? 0) },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading">This Month&apos;s Usage</CardTitle>
          <span className="px-2 py-0.5 rounded-full bg-primary-600 text-white text-xs font-semibold">
            Pro
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-[var(--border-default)]">
          {proStats.map((stat) => (
            <div key={stat.label} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
              <span className="text-sm text-[var(--text-secondary)]">{stat.label}</span>
              <span className="text-sm font-medium text-[var(--text-heading)]">{stat.value}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--border-default)]">
          <Link
            href="/settings"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <Settings className="w-3.5 h-3.5" />
            Manage plan
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ACTIVITY ITEM COMPONENT
// =============================================================================

interface DashboardActivityItem {
  id: string;
  type: 'import' | 'bulk' | 'recurring';
  title: string;
  description: string;
  time: string;
  status: OperationStatus;
  progress?: number;
  processedCount?: number;
  totalCount?: number;
  successCount?: number;
  errorCount?: number;
  canRollback?: boolean;
  boardId?: string;
  boardName?: string;
}

function ActivityItem({
  type,
  title,
  description,
  time,
  status,
  progress,
  processedCount,
  totalCount,
  successCount,
  errorCount,
  canRollback,
  boardId,
  boardName,
}: DashboardActivityItem) {
  const { account, monday } = useMondayAuth();
  const icons = {
    import: <Upload className="w-4 h-4" />,
    bulk: <Zap className="w-4 h-4" />,
    recurring: <RefreshCw className="w-4 h-4" />,
  };

  const iconBgColors: Record<OperationStatus, string> = {
    pending: 'bg-[var(--surface-hover)] text-[var(--text-secondary)]',
    parsing: 'bg-blue-100 text-blue-600',
    previewing: 'bg-blue-100 text-blue-600',
    confirmed: 'bg-blue-100 text-blue-600',
    processing: 'bg-blue-100 text-blue-600',
    completed: 'bg-green-100 text-green-600',
    partial_success: 'bg-amber-100 text-amber-600',
    failed: 'bg-red-100 text-red-600',
    cancelled: 'bg-[var(--surface-hover)] text-[var(--text-secondary)]',
    skipped: 'bg-[var(--surface-hover)] text-[var(--text-secondary)]',
    rolling_back: 'bg-amber-100 text-amber-600',
    rolled_back: 'bg-[var(--surface-hover)] text-[var(--text-secondary)]',
    partially_rolled_back: 'bg-amber-100 text-amber-600',
  };

  const showProgressBar = isProcessingStatus(status) && progress !== undefined;
  const showResultBar =
    isTerminalStatus(status) &&
    status !== 'failed' &&
    (errorCount ?? 0) > 0 &&
    totalCount !== undefined &&
    totalCount > 0;

  return (
    <div className="flex items-start gap-3 py-3">
      <div className={`p-2 rounded-lg ${iconBgColors[status] || 'bg-[var(--surface-hover)] text-[var(--text-secondary)]'}`}>
        {icons[type]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[var(--text-heading)] truncate">{title}</p>
          <StatusBadge status={status} size="sm" showIcon iconOnly={false} />
        </div>
        <p className="text-sm text-[var(--text-secondary)] truncate">{description}</p>
        {boardName && boardId && account?.slug && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const url = `https://${account.slug}.monday.com/boards/${boardId}`;
              const win = window.open(url, '_blank');
              if (!win) {
                try { monday.execute("openLinkInTab", { url }); } catch { /* noop */ }
              }
            }}
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline mt-0.5"
          >
            {boardName}
            <ExternalLink className="w-3 h-3" />
          </button>
        )}

        {showProgressBar && (
          <SimpleProgressBar value={progress!} size="sm" className="mt-2" />
        )}

        {showResultBar && (
          <OperationProgressBar
            total={totalCount!}
            processed={processedCount ?? totalCount!}
            successCount={successCount ?? 0}
            errorCount={errorCount ?? 0}
            size="sm"
            className="mt-2"
          />
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-[var(--text-muted)]">{time}</p>
        {canRollback && (
          <span className="text-xs text-green-600 flex items-center justify-end gap-1 mt-0.5">
            <Undo2 className="w-3 h-3" />
            Rollback
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// USAGE METER COMPONENT
// =============================================================================

interface UsageMeterProps {
  label: string;
  used: number;
  limit: number | 'unlimited';
}

function UsageMeter({ label, used, limit }: UsageMeterProps) {
  const isUnlimited = limit === 'unlimited';

  if (isUnlimited) {
    return (
      <div className="flex justify-between text-sm">
        <span className="text-[var(--text-secondary)]">{label}</span>
        <span className="font-medium text-green-600">Unlimited</span>
      </div>
    );
  }

  const percentage = Math.min((used / (limit as number)) * 100, 100);

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-[var(--text-secondary)]">{label}</span>
        <span className="font-medium text-[var(--text-heading)]">
          {used} / {limit}
        </span>
      </div>
      <Progress value={percentage} className="h-2" />
    </div>
  );
}

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================

export default function DashboardPage() {
  const { user, organization } = useMondayAuth();
  const subscriptionTier = useSubscriptionTier();
  const [stats, setStats] = useState({
    operationsToday: 0,
    activeOperations: 0,
    activeRecurring: 0,
    successRate: 0,
    itemsProcessed: 0,
  });
  const [recentActivity, setRecentActivity] = useState<DashboardActivityItem[]>([]);
  const [usageStats, setUsageStats] = useState<{
    bulkOperations: { used: number; limit: number };
    recurringPatterns: { used: number; limit: number };
    csvImports: { used: number; limit: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Fetch dashboard data
  useEffect(() => {
    if (!organization) return;

    const fetchStats = async () => {
      try {
        const response = await fetch(
          `/api/dashboard/stats?organizationId=${organization.id}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.stats) {
            setStats({
              operationsToday: data.stats.operationsToday || 0,
              activeOperations: data.stats.activeOperations || 0,
              activeRecurring: data.stats.activeRecurring || 0,
              successRate: data.stats.successRate || 0,
              itemsProcessed: data.stats.itemsProcessed || 0,
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };

    const fetchRecentOperations = async () => {
      try {
        const response = await fetch(
          `/api/bulk-operations/history?organizationId=${organization.id}&limit=5`
        );
        if (response.ok) {
          const data = await response.json();
          const activities: DashboardActivityItem[] = (data.operations || []).map((op: any) => {
            const mapType = (type: string): 'import' | 'bulk' | 'recurring' => {
              if (type?.includes('import') || type?.includes('csv')) return 'import';
              if (type?.includes('recurring') || type?.includes('scheduled')) return 'recurring';
              return 'bulk';
            };

            const formatTitle = () => {
              if (op.operation_title) return op.operation_title;
              const type = op.type?.replace(/_/g, ' ') || 'Operation';
              return type.charAt(0).toUpperCase() + type.slice(1);
            };

            const canRollback =
              !!op.rollback_data &&
              op.rollback_expires_at &&
              new Date(op.rollback_expires_at) > new Date() &&
              !op.is_rolled_back;

            const progress =
              op.total_count && op.total_count > 0
                ? Math.round((op.processed_count || 0) / op.total_count * 100)
                : op.progress_percentage ?? undefined;

            return {
              id: op.id,
              type: mapType(op.type),
              title: formatTitle(),
              description: `${op.processed_count || op.total_count || 0} items`,
              time: formatRelativeTime(new Date(op.created_at)),
              status: op.status as OperationStatus,
              progress,
              processedCount: op.processed_count ?? undefined,
              totalCount: op.total_count ?? undefined,
              successCount: op.success_count ?? undefined,
              errorCount: op.error_details?.length ?? 0,
              canRollback,
              boardId: op.configuration?.board_id || op.configuration?.boardId,
              boardName: (op.configuration?.board_name || op.configuration?.boardName) as string | undefined,
            };
          });
          setRecentActivity(activities);
        }
      } catch (err) {
        console.error('Failed to fetch activity:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUsageStats = async () => {
      try {
        const response = await fetch(
          `/api/usage-stats?org_id=${organization.id}`
        );
        if (response.ok) {
          const data = await response.json();
          setUsageStats({
            bulkOperations: data.bulkOperations || { used: 0, limit: 5 },
            recurringPatterns: data.recurringPatterns || { used: 0, limit: 10 },
            csvImports: data.csvImports || { used: 0, limit: -1 },
          });
        }
      } catch (err) {
        console.error('Failed to fetch usage stats:', err);
      }
    };

    fetchStats();
    fetchRecentOperations();
    fetchUsageStats();
  }, [organization]);

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const isPro = subscriptionTier !== 'free';
  const isNewUser = stats.operationsToday === 0 && stats.itemsProcessed === 0 && recentActivity.length === 0 && !loading;

  // ─── New User Layout ──────────────────────────────────────────────────
  if (isNewUser) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold font-heading text-[var(--text-heading)]">
            Welcome to TaskLoops, {firstName}!
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Let&apos;s get you set up. Most teams start with a CSV import — it&apos;s free and takes about 2 minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Action Cards */}
          <div className="lg:col-span-3 space-y-4">
            <FeatureActionCard
              icon={Upload}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              title="CSV / Excel Import"
              description="Upload a spreadsheet and map columns to your board with AI. Preview every row before it runs."
              tier="Free and unlimited"
              href="/csv-import"
              ctaLabel="Start importing"
            />
            <FeatureActionCard
              icon={Zap}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              title="Bulk Operations"
              description="Update, delete, or modify hundreds of items at once. Filter by any column, preview changes, then execute."
              tier="5 free per month"
              href="/bulk-operations"
              ctaLabel="Try bulk ops"
            />
            <FeatureActionCard
              icon={RefreshCw}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              title="Recurring Tasks"
              description="Automatically create items on a schedule — daily, weekly, or custom. Set it and forget it."
              tier="10 free per month"
              href="/recurring-patterns"
              ctaLabel="Set up a schedule"
            />

            {/* Empty State */}
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-[var(--text-secondary)] mb-3">Ready to get started?</p>
                <Link
                  href="/csv-import"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  <Upload className="w-4 h-4" />
                  Import your first CSV
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Usage Panel */}
          {isPro ? (
            <UsagePanelPro usageStats={usageStats} activeRecurring={stats.activeRecurring} />
          ) : (
            <UsagePanelFree usageStats={usageStats} onUpgrade={() => setShowUpgradeModal(true)} />
          )}
        </div>

        <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} featureType="bulk_operation" />
      </div>
    );
  }

  // ─── Returning User Layout ────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold font-heading text-[var(--text-heading)]">
          Welcome back, {firstName}
        </h1>
        <p className="text-[var(--text-secondary)] mt-1">
          Here&apos;s what&apos;s happening with your monday.com boards.
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Operations Today"
          value={stats.operationsToday}
        />
        <StatCard
          title="Active Recurring"
          value={stats.activeRecurring}
        />
        <StatCard
          title="Success Rate"
          value={`${stats.successRate}%`}
          change="Last 30 days"
        />
        <StatCard
          title="Items Processed"
          value={stats.itemsProcessed.toLocaleString()}
          change="Last 30 days"
        />
      </div>

      {/* Two Column Layout: Activity + Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading">Recent Activity</CardTitle>
            <Link
              href="/activity"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-[var(--text-muted)]">
                Loading activity...
              </div>
            ) : recentActivity.length > 0 ? (
              <div className="divide-y divide-[var(--border-default)]">
                {recentActivity.map((activity) => (
                  <ActivityItem key={activity.id} {...activity} />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-[var(--text-muted)]">
                <p>No recent activity</p>
                <p className="text-sm mt-1">
                  Start by importing a CSV or running a bulk operation
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Panel */}
        {isPro ? (
          <UsagePanelPro usageStats={usageStats} activeRecurring={stats.activeRecurring} />
        ) : (
          <UsagePanelFree usageStats={usageStats} onUpgrade={() => setShowUpgradeModal(true)} />
        )}
      </div>

      <UpgradeModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} featureType="bulk_operation" />
    </div>
  );
}
